import React, { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { DriverReviewReason, DriverReviewSample, DriverReviewsSummary, InsightsPeriod } from 'types/PropertyInsights';
import { HTTPError } from 'clients/HTTPClient';
import { propertyInsightsClient } from 'clients';
import { PagedResponse } from 'types/Pagination';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import { formatCount, insightsPeriodOptions, ReviewsRating, reviewsRatingOptions } from '../helpers';
import { DriverReviewsModalSkeleton, SampleSkeleton } from './DriverReviewsSkeleton';
import { SampleCommentAndRating } from './Samples';
import Summary from './Summary';
import Reasons from './Reasons';
import styles from './DriverReviewsWidget.module.css';

type ReviewModalProps = {
    samples: PagedResponse<DriverReviewSample>;
    filters: { reviewsPeriod: InsightsPeriod; rating: ReviewsRating };
    spotId: string;
    handleReviewsPeriodSelection: (selection: string) => void;
    handleRatingSelection: (selection: string) => void;
    isLoading: boolean;
    reasons: DriverReviewReason[] | null;
    summary: DriverReviewsSummary | null;
};

export function ReviewModal({
    samples,
    spotId,
    filters,
    handleReviewsPeriodSelection,
    handleRatingSelection,
    isLoading,
    reasons,
    summary,
}: ReviewModalProps) {
    const [page, setPage] = useState(1);
    const [allSamples, setAllSamples] = useState(samples.results);
    const [hasNext, setHasNext] = useState(Boolean(samples.next));
    const [loadingSamples, setLoadingSamples] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const loadMore = useCallback(async () => {
        try {
            const nextPage = page + 1;
            const newSamples = await propertyInsightsClient.getDriverReviewSamples(
                spotId,
                filters.reviewsPeriod,
                filters.rating,
                nextPage
            );
            setPage(nextPage);
            setAllSamples([...allSamples, ...newSamples.results]);
            setHasNext(Boolean(newSamples.next));
        } catch (e) {
            console.error(e); // Silently fail
            Sentry.captureException(e);
            setHasNext(false);
        }
    }, [page, spotId, filters.reviewsPeriod, filters.rating]);

    const onRatingChange = useCallback(async () => {
        setLoadingSamples(true);
        setErrorMessage('');
        try {
            const newSamples = await propertyInsightsClient.getDriverReviewSamples(
                spotId,
                filters.reviewsPeriod,
                filters.rating,
                1
            );
            setAllSamples(newSamples.results);
            setHasNext(Boolean(newSamples.next));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
            setAllSamples([]);
            setHasNext(false);
        } finally {
            setPage(1);
            setLoadingSamples(false);
        }
    }, [spotId, filters.reviewsPeriod, filters.rating]);

    useEffect(() => {
        onRatingChange();
    }, [filters.rating]);

    const target = useInfiniteScroll({ loadMore });

    if (!summary || !reasons || isLoading) return <DriverReviewsModalSkeleton />;

    return (
        <div className={styles.reviewsModalContent}>
            <div className={styles.reviewsModalColumn}>
                <AGSimpleSelect
                    options={insightsPeriodOptions}
                    selected={filters.reviewsPeriod}
                    onSelect={handleReviewsPeriodSelection}
                    width="160px"
                />
                <Summary summary={summary} />
                <Reasons reasons={reasons} />
            </div>
            <div className={styles.reviewsModalColumn}>
                <div className={styles.reviewsModalHeader}>
                    <h3>What They Say</h3>
                    <p className={styles.reviewsModalCount}>{formatCount(samples.count)} Reviews with Comments</p>
                </div>
                <AGSimpleSelect
                    options={reviewsRatingOptions}
                    selected={filters.rating}
                    onSelect={handleRatingSelection}
                    width="160px"
                />
                {loadingSamples ? (
                    <SampleSkeleton />
                ) : errorMessage ? (
                    <WidgetErrorMessage message={errorMessage} onRetry={onRatingChange} />
                ) : (
                    allSamples.map((sample) => <SampleCommentAndRating sample={sample} key={sample.review_id} />)
                )}
                {hasNext && <span ref={target} id="scroll-target" />}
            </div>
        </div>
    );
}
